import { useEffect, useState } from 'react';

import { bindAll } from 'bind-event-listener';
import omit from 'lodash/omit';
import { useDebouncedCallback } from 'use-debounce';

import { type Ati } from '@atlassian/ari/ati';

import type { MixedMenuItems, SimpleMenuItems, UniversalCreateItems } from './types';
import type { SuggestionConfig, CrossAppCreationConfig } from './types';
import type { UniversalCreateMenuSection, NativeMenuItem } from './types/menu';

export function isSimpleMenuItems(items: UniversalCreateItems): items is SimpleMenuItems {
	if (Array.isArray(items)) {
		return items.every((item) => {
			return typeof item === 'object' && item !== null && 'ati' in item;
		});
	}

	return false;
}

/**
 * V2 version of findSelectedItem that searches through UniversalCreateMenuSection array.
 * Searches app-defined sections for items matching the selected ATI.
 * Note: Suggestion sections don't contain items at definition time, so they're skipped.
 *
 * @param sections - Array of UniversalCreateMenuSection to search through
 * @param selectedItem - The ATI to search for
 * @returns The matching NativeMenuItem or null if not found
 */
export function findSelectedItem(
	sections: UniversalCreateMenuSection[],
	selectedItem: Ati,
): NativeMenuItem | null {
	for (const section of sections) {
		// Only app-defined sections have items at definition time
		if (section.type === 'appDefinedSection') {
			for (const menuItem of section.items) {
				// Check if item has ati property (both NativeMenuItem and CrossAppMenuItem have it)
				if ('ati' in menuItem && menuItem.ati.toString() === selectedItem.toString()) {
					// For cross-app items, we need to return null as they don't have onClick/loadCreateContent
					// Only native items can be "selected" in the traditional sense
					if ('onClick' in menuItem || 'loadCreateContent' in menuItem) {
						return menuItem;
					}
				}
			}
		}
	}
	return null;
}

/**
 * Extracts all ATIs from the mixed menu items
 * @param mixedItems - The mixed menu items to extract ATIs from
 * @returns Array of ATI strings
 */
export function extractNativeAtis(mixedItems: MixedMenuItems): string[] {
	return mixedItems
		.flatMap((namedItem) => namedItem.items)
		.map((menuItem) => menuItem.ati.toString());
}

export const RESIZING_DEBOUNCED = 100;

const MOBILE_WIDTH_BREAKPOINT = 767;

export function useIsResponsiveView(): boolean {
	const [isResponsiveMode, setIsResponsiveMode] = useState(
		window.innerWidth <= MOBILE_WIDTH_BREAKPOINT,
	);

	const [handleResizeDebounced] = useDebouncedCallback(() => {
		const isCurrentlyMobile = window.innerWidth <= MOBILE_WIDTH_BREAKPOINT;
		setIsResponsiveMode((prev) => (prev !== isCurrentlyMobile ? isCurrentlyMobile : prev));
	}, RESIZING_DEBOUNCED);

	useEffect(() => {
		let unbind;
		unbind = bindAll(window, [
			{
				type: 'resize',
				listener: handleResizeDebounced,
			},
		]);

		return unbind;
	}, [handleResizeDebounced]);

	return isResponsiveMode;
}

// A safer omit, omitted properties must exist on the item type
export const omitSafely = <T extends object, P extends (keyof T)[]>(
	item: T,
	...properties: P
): Omit<T, P[number]> => omit(item, ...properties);

/**
 * Get the cloud ID from either V1 (suggestionConfig) or V2 (crossAppCreationConfig) configuration.
 */
export const getCloudId = (contextConfig: SuggestionConfig | CrossAppCreationConfig): string =>
	contextConfig.enabled ? contextConfig.cloudId : '';

/**
 * Get the org ID from either V1 (suggestionConfig) or V2 (crossAppCreationConfig) configuration.
 */
export const getOrgId = (
	contextConfig: SuggestionConfig | CrossAppCreationConfig,
): string | undefined => (contextConfig.enabled ? contextConfig.orgId : undefined);
